import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Paper,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { MessageKey, useI18n } from '../i18n';
import { RootState } from '../store';

import { PageShell } from './components/PageShell';
import { dayKey } from './draw';
import {
    exportJournal,
    JOURNAL_MAX_LENGTH,
    journalStreak,
    promptIndex,
} from './journal';
import { clearJournal, saveJournalEntry } from './tarotSlice';

/** A private one-line-a-day journal. Everything stays in the app's local storage. */
export const JournalPage: React.FC = () => {
    const { t, locale } = useI18n();
    const dispatch = useDispatch();
    const entries = useSelector((s: RootState) => s.tarot.journal);
    const today = dayKey();
    const todays = entries.find((e) => e.day === today);
    const [text, setText] = useState(todays?.text ?? '');
    const [confirming, setConfirming] = useState(false);
    const streak = journalStreak(
        entries.map((e) => e.day),
        today
    );
    const past = entries.filter((e) => e.day !== today);

    const save = () => {
        const line = text.trim();
        if (!line) return;
        dispatch(saveJournalEntry({ day: today, text: line, at: Date.now() }));
    };

    const download = () => {
        const blob = new Blob([exportJournal(entries)], {
            type: 'application/json',
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'arcana-journal.json';
        a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <PageShell to="/">
            <Typography variant="h4" component="h1" gutterBottom>
                {t('journal.title')}
            </Typography>
            <Typography
                color="text.secondary"
                gutterBottom
                data-testid="journal-streak"
            >
                {streak > 0
                    ? t('journal.streak', { n: streak })
                    : t('journal.streak0')}
            </Typography>
            <Paper sx={{ p: 2, mb: 2, background: 'rgba(21,14,36,.85)' }}>
                <Typography variant="overline" color="text.secondary">
                    {t('journal.today')}
                </Typography>
                <Typography paragraph>
                    {t(`journal.prompt${promptIndex(today)}` as MessageKey)}
                </Typography>
                <TextField
                    fullWidth
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    placeholder={t('journal.placeholder')}
                    inputProps={{
                        maxLength: JOURNAL_MAX_LENGTH,
                        'aria-label': t('journal.placeholder'),
                    }}
                />
                <Stack direction="row" alignItems="center" spacing={2} mt={1.5}>
                    <Button
                        variant="contained"
                        onClick={save}
                        disabled={!text.trim()}
                    >
                        {t('journal.save')}
                    </Button>
                    {todays && todays.text === text.trim() && (
                        <Typography variant="caption" color="text.secondary">
                            {t('journal.saved')}
                        </Typography>
                    )}
                </Stack>
            </Paper>
            <Typography variant="h6" component="h2" gutterBottom>
                {t('journal.past')}
            </Typography>
            {past.length === 0 && (
                <Typography color="text.secondary">
                    {t('journal.empty')}
                </Typography>
            )}
            <Stack spacing={1} data-testid="journal-list">
                {past.map((e) => (
                    <Paper
                        key={e.day}
                        sx={{ p: 1.5, background: 'rgba(21,14,36,.85)' }}
                    >
                        <Typography variant="caption" color="text.secondary">
                            {new Date(
                                Number(e.day.slice(0, 4)),
                                Number(e.day.slice(5, 7)) - 1,
                                Number(e.day.slice(8, 10))
                            ).toLocaleDateString(locale)}
                        </Typography>
                        <Typography>{e.text}</Typography>
                    </Paper>
                ))}
            </Stack>
            <Box sx={{ mt: 3 }}>
                <Typography variant="caption" color="text.secondary" paragraph>
                    {t('journal.privacy')}
                </Typography>
                <Stack direction="row" spacing={1}>
                    <Button
                        variant="outlined"
                        onClick={download}
                        disabled={entries.length === 0}
                    >
                        {t('journal.export')}
                    </Button>
                    <Button
                        variant="outlined"
                        color="error"
                        onClick={() => setConfirming(true)}
                        disabled={entries.length === 0}
                    >
                        {t('journal.deleteAll')}
                    </Button>
                </Stack>
            </Box>
            <Dialog open={confirming} onClose={() => setConfirming(false)}>
                <DialogTitle>{t('journal.confirmTitle')}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t('journal.confirmBody')}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirming(false)}>
                        {t('journal.cancel')}
                    </Button>
                    <Button
                        color="error"
                        onClick={() => {
                            dispatch(clearJournal());
                            setText('');
                            setConfirming(false);
                        }}
                    >
                        {t('journal.confirm')}
                    </Button>
                </DialogActions>
            </Dialog>
        </PageShell>
    );
};
